import React, { ReactElement } from 'react';
import { XIcon } from 'lucide-react';
import { PlayIcon } from 'src/assets/icons/PlayIcon';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { Button } from 'src/domains/common/components/button/button';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { observer } from 'src/utils/mobx-react';
import { MarkersOfHarmPopupConfigurationSummary } from './MarkersOfHarmPopupConfigurationSummary';
import { MarkersOfHarmPopupFormState } from './MarkersOfHarmPopupForm.state';
import { MarkersOfHarmRiskLevelField } from './MarkersOfHarmRiskLevelField';
import {
    POPUP_APPLIED_ACTION_OPTIONS,
    getPopupRollingPeriodOptions,
    POPUP_PRIMARY_BUTTON_ACTION_OPTIONS,
    POPUP_SECONDARY_BUTTON_ACTION_OPTIONS,
    POPUP_TITLE_MAX_LENGTH,
    POPUP_BUTTON_TEXT_MAX_LENGTH,
    getAppliedActionRestrictionPeriodOptions,
} from './MarkersOfHarmPopupTab.types';

import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';

const markFieldVisitedWhenOverMaxLength = (
    formInput: FormInputState<string, string>,
    maxLength: number,
    value: string
): void => {
    if (value.length > maxLength) {
        formInput.setAsVisited();
    }
};

const iconButtonClassName =
    'inline-flex size-9 items-center justify-center rounded-md border border-solid border-input bg-icon-button shadow-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-50';

const RequiredLabel = ({ children }: { children: React.ReactNode }): ReactElement => (
    <span className='text-sm'>
        {children}
        <span className='text-destructive'> *</span>
    </span>
);

type MarkersOfHarmPopupFormPropsType = {
    formState: MarkersOfHarmPopupFormState;
    isSubmitting?: boolean;
    onSubmit: () => void;
    onPreview: () => void;
    onClose: () => void;
};

export const MarkersOfHarmPopupForm = observer(
    'MarkersOfHarmPopupForm',
    ({
        formState,
        isSubmitting = false,
        onSubmit,
        onPreview,
        onClose,
    }: MarkersOfHarmPopupFormPropsType): ReactElement => {
        const primaryActionOptions = POPUP_PRIMARY_BUTTON_ACTION_OPTIONS.map((option) => ({
            label: option.label,
            value: option.value,
        }));
        const secondaryActionOptions = POPUP_SECONDARY_BUTTON_ACTION_OPTIONS.map((option) => ({
            label: option.label,
            value: option.value,
        }));
        const triggerTypeOptions = formState.triggerTemplateOptions.map((option) => ({
            label: option.label,
            value: option.value,
            description: option.description,
            onClick: (): void => {
                formState.onTriggerTypeOptionClick(option.value);
            },
        }));
        const rollingPeriodOptions = getPopupRollingPeriodOptions(formState.selectedTriggerTemplate).map((option) => ({
            label: option.label,
            value: option.value,
        }));
        const durationHoursOptions = Array.from({ length: 12 }, (_, index) => {
            const hours = index + 1;
            return {
                label: `${hours} ${hours === 1 ? 'hour' : 'hours'}`,
                value: String(hours),
            };
        });
        const utcHourOptions = Array.from({ length: 24 }, (_, hour) => {
            const hourLabel = String(hour).padStart(2, '0');
            const value = `${hourLabel}:00`;
            return {
                label: `${value} UTC`,
                value,
            };
        });
        const appliedActionOptions = POPUP_APPLIED_ACTION_OPTIONS.map((option) => ({
            label: option.label,
            value: option.value,
            description: option.description,
            onClick: formState.onAppliedActionOptionClick,
        }));
        const appliedAction = formState.appliedActionForm.value;
        const restrictionPeriodOptions = getAppliedActionRestrictionPeriodOptions(appliedAction);
        const showAppliedActionRestrictionPeriod = formState.showAppliedActionRestrictionPeriod;

        const canPreview = formState.buildPreviewContent() !== null;
        const canDiscardChanges = formState.isEditMode || formState.hasUnsavedChanges;

        return (
            <form
                className='flex flex-col gap-5'
                onSubmit={(event): void => {
                    event.preventDefault();
                    onSubmit();
                }}
            >
                <div className='flex items-start justify-between gap-4'>
                    <h1
                        className='m-0 text-[24px] font-semibold leading-[32px] text-foreground'
                        data-test='markers-of-harm-popup-form-title'
                    >
                        {formState.isEditMode ? 'Edit Pop-Up' : 'Create Pop-Up'}
                    </h1>
                    <div className='flex items-center gap-2 shrink-0'>
                        <button
                            type='button'
                            className={iconButtonClassName}
                            aria-label='Preview Pop-Up'
                            data-test='markers-of-harm-popup-preview-trigger'
                            disabled={!canPreview}
                            onClick={onPreview}
                        >
                            <PlayIcon />
                        </button>
                        <button
                            type='button'
                            className={iconButtonClassName}
                            aria-label='Close without saving'
                            data-test='markers-of-harm-popup-close-form'
                            disabled={!canDiscardChanges}
                            onClick={onClose}
                        >
                            <XIcon className='size-4' />
                        </button>
                    </div>
                </div>
                <section
                    className='flex flex-col gap-6'
                    data-test='markers-of-harm-popup-content-section'
                >
                    <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>Pop-Up content</h2>
                    <div className='flex flex-col gap-4'>
                        <Input
                            label={<RequiredLabel>Title (max {POPUP_TITLE_MAX_LENGTH} characters)</RequiredLabel>}
                            placeholder='Add a title...'
                            formInputState={formState.titleForm}
                            errors={formState.titleForm.errorsForView}
                            dataTest='markers-of-harm-popup-title'
                            onChange={(event): void => {
                                markFieldVisitedWhenOverMaxLength(
                                    formState.titleForm,
                                    POPUP_TITLE_MAX_LENGTH,
                                    event.target.value
                                );
                            }}
                        />
                        <div className='flex flex-col gap-2'>
                            <RequiredLabel>Body text</RequiredLabel>
                            <Textarea
                                formState={formState.bodyTextForm}
                                ckeditor
                                dataTest='markers-of-harm-popup-body'
                                customErrorMessage={
                                    formState.bodyTextForm.errorsForView.length > 0 ? (
                                        <span className='text-sm text-destructive mt-2'>
                                            {formState.bodyTextForm.errorsForView[0]}
                                        </span>
                                    ) : undefined
                                }
                            />
                        </div>
                    </div>
                </section>

                <section
                    className='flex flex-col gap-6'
                    data-test='markers-of-harm-popup-buttons-section'
                >
                    <div className='flex flex-col gap-2'>
                        <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>Pop-Up buttons</h2>
                        <p className='m-0 text-sm text-muted-foreground'>
                            Add up to two buttons and define what each one does.
                        </p>
                    </div>

                    <div className='flex flex-col gap-6'>
                        <div className='flex flex-col gap-4'>
                            <Input
                                label={
                                    <RequiredLabel>
                                        Primary (max {POPUP_BUTTON_TEXT_MAX_LENGTH} characters)
                                    </RequiredLabel>
                                }
                                placeholder='Enter text'
                                formInputState={formState.primaryButtonTextForm}
                                errors={formState.primaryButtonTextForm.errorsForView}
                                dataTest='markers-of-harm-popup-primary-text'
                                onChange={(event): void => {
                                    markFieldVisitedWhenOverMaxLength(
                                        formState.primaryButtonTextForm,
                                        POPUP_BUTTON_TEXT_MAX_LENGTH,
                                        event.target.value
                                    );
                                }}
                            />
                            <Select
                                key='markers-of-harm-popup-primary-action'
                                placeholder='Select action'
                                formInputState={formState.primaryButtonActionForm}
                                values={primaryActionOptions}
                                errors={formState.primaryButtonActionForm.errorsForView}
                                dataTest='markers-of-harm-popup-primary-action'
                            />
                            {formState.showPrimaryRedirectUrl && (
                                <Input
                                    placeholder='Enter URL'
                                    formInputState={formState.primaryRedirectUrlForm}
                                    errors={formState.primaryRedirectUrlForm.errorsForView}
                                    dataTest='markers-of-harm-popup-primary-url'
                                />
                            )}
                        </div>

                        {formState.hasSecondaryButton ? (
                            <div className='flex flex-col gap-4'>
                                <div className='flex items-center justify-between'>
                                    <RequiredLabel>
                                        Secondary (max {POPUP_BUTTON_TEXT_MAX_LENGTH} characters)
                                    </RequiredLabel>
                                    <IconButton
                                        type='button'
                                        variant='ghost'
                                        size='sm'
                                        icon='trash'
                                        aria-label='Remove secondary button'
                                        dataTest='markers-of-harm-popup-remove-secondary'
                                        onClick={formState.removeSecondaryButton}
                                    />
                                </div>
                                <Input
                                    placeholder='Enter text'
                                    formInputState={formState.secondaryButtonTextForm}
                                    errors={formState.secondaryButtonTextForm.errorsForView}
                                    dataTest='markers-of-harm-popup-secondary-text'
                                    onChange={(event): void => {
                                        markFieldVisitedWhenOverMaxLength(
                                            formState.secondaryButtonTextForm,
                                            POPUP_BUTTON_TEXT_MAX_LENGTH,
                                            event.target.value
                                        );
                                    }}
                                />
                                <Select
                                    key='markers-of-harm-popup-secondary-action'
                                    placeholder='Select action'
                                    formInputState={formState.secondaryButtonActionForm}
                                    values={secondaryActionOptions}
                                    errors={formState.secondaryButtonActionForm.errorsForView}
                                    dataTest='markers-of-harm-popup-secondary-action'
                                />
                                {formState.showSecondaryRedirectUrl && (
                                    <Input
                                        placeholder='Enter URL'
                                        formInputState={formState.secondaryRedirectUrlForm}
                                        errors={formState.secondaryRedirectUrlForm.errorsForView}
                                        dataTest='markers-of-harm-popup-secondary-url'
                                    />
                                )}
                            </div>
                        ) : (
                            <Button
                                type='button'
                                variant='link'
                                icon='plus'
                                className='self-start h-auto px-0'
                                dataTest='markers-of-harm-popup-add-secondary'
                                onClick={formState.addSecondaryButton}
                            >
                                Add secondary button
                            </Button>
                        )}
                    </div>
                </section>

                <section
                    className='flex flex-col gap-6 [border-top:1px_solid_#E4E4E7] pt-6'
                    data-test='markers-of-harm-popup-configuration-section'
                >
                    <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>
                        Pop-Up configuration
                    </h2>
                    <MarkersOfHarmPopupConfigurationSummary
                        formState={formState}
                        appliedAction={formState.appliedActionForm.value}
                        appliedActionRestrictionPeriod={formState.appliedActionRestrictionPeriodForm.value}
                        riskLevel={formState.riskLevel}
                    />
                    <div className='flex flex-col gap-2'>
                        <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>
                            Trigger settings
                        </h2>
                        <p className='m-0 text-sm text-muted-foreground'>
                            Set when the Pop-Up appears based on user actions.
                        </p>
                    </div>
                    <div
                        className='flex flex-col gap-4 [&_label]:first-letter:uppercase [&_span.block]:first-letter:uppercase'
                        data-test='markers-of-harm-popup-trigger-section'
                    >
                        <Select
                            key='markers-of-harm-popup-trigger-type'
                            label={<RequiredLabel>Trigger template</RequiredLabel>}
                            description={
                                formState.hasDisabledTemplateSelectorDescription
                                    ? 'Trigger template cannot be changed during edit.'
                                    : undefined
                            }
                            placeholder='Select template'
                            formInputState={formState.triggerTypeForm}
                            values={triggerTypeOptions}
                            errors={formState.triggerTypeForm.errorsForView}
                            disabled={formState.isEditMode}
                            dataTest='markers-of-harm-popup-trigger-type'
                        />
                        {formState.missingTriggerTemplateForEdit ? (
                            <p className='m-0 text-sm text-destructive'>
                                This Pop-Up uses a trigger template that is no longer available in the API.
                            </p>
                        ) : null}
                        {formState.triggerParamFields.map((field) => {
                            const fieldKey = `markers-of-harm-popup-trigger-param-${formState.triggerTypeForm.value}-${formState.editingPopupId ?? 'new'}-${field.param.name}-${field.kind}-v${formState.triggerParamFieldsVersion}`;
                            if (field.kind === 'scalar') {
                                return (
                                    <Input
                                        key={fieldKey}
                                        label={<RequiredLabel>{field.param.displayName}</RequiredLabel>}
                                        placeholder='Enter value'
                                        inputMode={
                                            field.param.type === 'integer' ||
                                            field.param.type === 'numeric' ||
                                            field.param.type === 'duration'
                                                ? 'decimal'
                                                : 'text'
                                        }
                                        formInputState={field.input}
                                        errors={field.input.errorsForView}
                                        dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}`}
                                    />
                                );
                            }

                            if (field.kind === 'boolean') {
                                return (
                                    <Select
                                        key={fieldKey}
                                        label={<RequiredLabel>{field.param.displayName}</RequiredLabel>}
                                        placeholder='Select value'
                                        formInputState={field.input}
                                        values={[
                                            { label: 'True', value: 'true' },
                                            { label: 'False', value: 'false' },
                                        ]}
                                        errors={field.input.errorsForView}
                                        dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}`}
                                    />
                                );
                            }

                            if (field.kind === 'duration-hours') {
                                return (
                                    <Select
                                        key={fieldKey}
                                        label={<RequiredLabel>{field.param.displayName}</RequiredLabel>}
                                        placeholder='Select hours'
                                        formInputState={field.input}
                                        values={durationHoursOptions}
                                        errors={field.input.errorsForView}
                                        dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}`}
                                    />
                                );
                            }

                            if (field.kind === 'period-rolling') {
                                return (
                                    <Select
                                        key={fieldKey}
                                        label={<RequiredLabel>{field.param.displayName}</RequiredLabel>}
                                        placeholder='Select rolling period'
                                        formInputState={field.input}
                                        values={rollingPeriodOptions}
                                        errors={field.input.errorsForView}
                                        dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}`}
                                    />
                                );
                            }

                            return (
                                <div
                                    key={fieldKey}
                                    className='flex flex-col gap-2'
                                >
                                    <p className='m-0 text-sm text-muted-foreground'>
                                        Hour from and hour to are based on UTC time.
                                    </p>
                                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                        <Select
                                            key={`${fieldKey}-start`}
                                            label={<RequiredLabel>{`${field.param.displayName} from`}</RequiredLabel>}
                                            placeholder='Select UTC hour'
                                            formInputState={field.startInput}
                                            values={utcHourOptions}
                                            errors={field.startInput.errorsForView}
                                            dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}-start`}
                                        />
                                        <Select
                                            key={`${fieldKey}-end`}
                                            label={<RequiredLabel>{`${field.param.displayName} to`}</RequiredLabel>}
                                            placeholder='Select UTC hour'
                                            formInputState={field.endInput}
                                            values={utcHourOptions}
                                            errors={field.endInput.errorsForView}
                                            dataTest={`markers-of-harm-popup-trigger-param-${field.param.name}-end`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section
                    className='flex flex-col gap-6'
                    data-test='markers-of-harm-popup-applied-actions-section'
                >
                    <div className='flex flex-col gap-2'>
                        <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>
                            Applied actions
                            <span className='text-destructive'> *</span>
                        </h2>
                        <p className='m-0 text-sm text-muted-foreground'>
                            Define the actions applied to the user&apos;s account when this Pop-Up is displayed. These
                            actions are applied automatically, regardless of user interaction.
                        </p>
                    </div>
                    <Select
                        key='markers-of-harm-popup-applied-action'
                        placeholder='Select action'
                        formInputState={formState.appliedActionForm}
                        values={appliedActionOptions}
                        errors={formState.appliedActionForm.errorsForView}
                        dataTest='markers-of-harm-popup-applied-action'
                    />
                    {showAppliedActionRestrictionPeriod ? (
                        <Select
                            key={`markers-of-harm-popup-restriction-period-${appliedAction}`}
                            label={<RequiredLabel>Restriction period</RequiredLabel>}
                            placeholder='Select period'
                            formInputState={formState.appliedActionRestrictionPeriodForm}
                            values={restrictionPeriodOptions}
                            errors={formState.appliedActionRestrictionPeriodForm.errorsForView}
                            dataTest='markers-of-harm-popup-applied-action-restriction-period'
                        />
                    ) : null}
                </section>

                <section
                    className='flex flex-col gap-6'
                    data-test='markers-of-harm-popup-risk-level-section'
                >
                    <div className='flex flex-col gap-2'>
                        <h2 className='m-0 text-[20px] font-semibold leading-[28px] text-foreground'>Risk level</h2>
                        <p className='m-0 text-sm text-muted-foreground'>
                            Mark a risk level for this Pop-Up (internal purpose).
                        </p>
                    </div>
                    <MarkersOfHarmRiskLevelField
                        value={formState.riskLevel}
                        onChange={formState.setRiskLevel}
                        dataTest='markers-of-harm-popup-risk-level'
                    />
                </section>

                <div className='flex items-center justify-between gap-4 p-4 [border-top:1px_solid_#E4E4E7]'>
                    <Button
                        type='button'
                        variant='outline'
                        disabled={!canDiscardChanges}
                        dataTest='markers-of-harm-popup-cancel'
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <div className='flex items-center gap-2'>
                        <Button
                            type='button'
                            variant='outline'
                            disabled={!canPreview}
                            dataTest='markers-of-harm-popup-preview'
                            onClick={onPreview}
                        >
                            Preview
                        </Button>
                        <Button
                            type='submit'
                            variant='primary'
                            disabled={isSubmitting || !formState.isFormValid}
                            dataTest='markers-of-harm-popup-submit'
                        >
                            {isSubmitting
                                ? formState.isEditMode
                                    ? 'Saving…'
                                    : 'Creating…'
                                : formState.isEditMode
                                  ? 'Save'
                                  : 'Create'}
                        </Button>
                    </div>
                </div>
            </form>
        );
    }
);
